import { useEffect, useId, useRef, useState } from 'react';
import { parseInput } from '../../geo/parseInput';
import { candidateToBuilding, findEgid, searchSwiss, searchWorld, type Candidate } from '../../geo/services';
import type { Building } from '../../core/domain/types';
import { IconSearch } from '../icons';
import { useDismiss } from '../hooks';

type Status = { kind: 'idle' } | { kind: 'loading' } | { kind: 'error'; text: string } | { kind: 'none'; text: string } | { kind: 'ok' };

interface Props {
  onPick: (b: Building) => void;
  disabled?: boolean;
  disabledReason?: string;
  autoFocus?: boolean;
  placeholder?: string;
  compact?: boolean;
}

export function LocationSearch({ onPick, disabled, disabledReason, autoFocus, placeholder, compact }: Props) {
  const [q, setQ] = useState('');
  const [items, setItems] = useState<Candidate[]>([]);
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [adding, setAdding] = useState(false);
  const listId = useId();
  const wrapRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  useDismiss(open, () => setOpen(false), wrapRef);

  useEffect(() => {
    const parsed = parseInput(q);
    abortRef.current?.abort();
    if (parsed.kind === 'empty') {
      setItems([]);
      setStatus({ kind: 'idle' });
      return;
    }
    if (parsed.kind === 'invalid') {
      setItems([]);
      setStatus({ kind: 'error', text: parsed.reason });
      return;
    }
    if (parsed.kind === 'coords' || parsed.kind === 'lv95') {
      const lat = parsed.lat;
      const lon = parsed.lon;
      setItems([
        {
          id: `xy-${lat}-${lon}`,
          source: 'koordinaten',
          kind: 'Koordinaten',
          label: `Koordinaten ${lat.toFixed(5)}, ${lon.toFixed(5)}`,
          sub: parsed.kind === 'lv95' ? `aus LV95 E ${Math.round(parsed.e)} / N ${Math.round(parsed.n)} · ohne Geocoder` : 'WGS84 · ohne Geocoder',
          lat,
          lon,
        },
      ]);
      setStatus({ kind: 'ok' });
      setActive(0);
      return;
    }
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setStatus({ kind: 'loading' });
    const t = setTimeout(async () => {
      try {
        let res: Candidate[] = [];
        if (parsed.kind === 'egid') {
          res = await findEgid(parsed.egid, ctrl.signal);
          if (!res.length) {
            setItems([]);
            setStatus({ kind: 'none', text: `Keine Schweizer Gebäude mit EGID ${parsed.egid} gefunden.` });
            return;
          }
        } else {
          const [ch, world] = await Promise.allSettled([searchSwiss(parsed.text, ctrl.signal), searchWorld(parsed.text, ctrl.signal)]);
          const chList = ch.status === 'fulfilled' ? ch.value : [];
          const wList = world.status === 'fulfilled' ? world.value.filter((w) => w.country !== 'CH' || chList.length === 0) : [];
          res = [...chList, ...wList];
          if (ch.status === 'rejected' && world.status === 'rejected') {
            if ((ch.reason as Error)?.name === 'AbortError') return;
            setItems([]);
            setStatus({ kind: 'error', text: 'Suchdienste nicht erreichbar. Koordinaten (z. B. «47.3765, 8.5482») funktionieren ohne Suchdienst.' });
            return;
          }
        }
        if (ctrl.signal.aborted) return;
        setItems(res);
        setActive(res.length ? 0 : -1);
        setStatus(res.length ? { kind: 'ok' } : { kind: 'none', text: 'Keine Treffer. Schreibweise prüfen oder Koordinaten eingeben.' });
      } catch (e) {
        if ((e as Error).name === 'AbortError') return;
        setItems([]);
        setStatus({ kind: 'error', text: (e as Error).message });
      }
    }, 280);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [q]);

  const pick = async (c: Candidate) => {
    setAdding(true);
    try {
      const b = await candidateToBuilding(c);
      onPick(b);
      setQ('');
      setItems([]);
      setOpen(false);
      setStatus({ kind: 'idle' });
    } catch (e) {
      setStatus({ kind: 'error', text: `Standort konnte nicht übernommen werden: ${(e as Error).message}` });
    } finally {
      setAdding(false);
    }
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((a) => Math.min(items.length - 1, a + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    } else if (e.key === 'Enter') {
      if (open && active >= 0 && items[active]) {
        e.preventDefault();
        void pick(items[active]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const groups: { title: string; list: { c: Candidate; i: number }[] }[] = [];
  items.forEach((c, i) => {
    const title = c.source === 'geo.admin.ch' ? 'Schweiz · geo.admin.ch' : c.source === 'open-meteo-geocoding' ? 'Weltweit · Open-Meteo Geocoding' : 'Koordinaten';
    let g = groups.find((x) => x.title === title);
    if (!g) groups.push((g = { title, list: [] }));
    g.list.push({ c, i });
  });

  const showList = open && (items.length > 0 || status.kind !== 'idle');
  return (
    <div className="search" ref={wrapRef}>
      <IconSearch className="search-icon" />
      <input
        className="search-input"
        style={compact ? { minHeight: 40 } : undefined}
        role="combobox"
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={active >= 0 && showList ? `${listId}-${active}` : undefined}
        aria-label="Standort suchen: Adresse, EGID, Ort oder Koordinaten"
        aria-invalid={status.kind === 'error'}
        placeholder={disabled ? disabledReason : (placeholder ?? 'Adresse, EGID, Ort oder Koordinaten (47.3765, 8.5482)')}
        value={q}
        disabled={disabled || adding}
        autoFocus={autoFocus}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
      />
      {showList && (
        <ul className="search-list" id={listId} role="listbox" aria-label="Suchtreffer">
          {status.kind === 'loading' && <li className="search-status">Suche läuft …</li>}
          {(status.kind === 'error' || status.kind === 'none') && (
            <li className="search-status" role="alert">
              {status.text}
            </li>
          )}
          {adding && <li className="search-status">Standort wird übernommen (Höhe, GWR) …</li>}
          {groups.map((g) => (
            <li key={g.title} role="presentation">
              <div className="search-group" aria-hidden="true">
                {g.title}
              </div>
              <ul role="group" aria-label={g.title} style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {g.list.map(({ c, i }) => (
                  <li
                    key={c.id}
                    id={`${listId}-${i}`}
                    role="option"
                    aria-selected={i === active}
                    className="search-option"
                    onMouseEnter={() => setActive(i)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => void pick(c)}
                  >
                    <span className="opt-main">{c.label}</span>
                    <span className="opt-sub">{c.sub}</span>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
